/* eslint-disable prettier/prettier */
/* eslint-disable max-len */
/* Copyright 2012 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// eslint-disable-next-line no-redeclare
/* globals PDFJS, FirefoxCom, Promise, scrollIntoView */

"use strict";

import { getCharacterType } from "./pdf_find_utils.js";

const FindStatus = {
  FIND_FOUND: 0,
  FIND_NOTFOUND: 1,
  FIND_WRAPPED: 2,
  FIND_PENDING: 3,
};

const FIND_SCROLL_OFFSET_TOP = -50;
const FIND_SCROLL_OFFSET_LEFT = -400;

/**
 * Scrolls specified element into view of its parent.
 * @param {Object} element - The element to be visible.
 * @param {Object} spot - An object with optional top and left properties,
 *   specifying the offset from the top left edge.
 * @param {boolean} skipOverflowHiddenElements - Ignore elements that have
 *   the CSS rule `overflow: hidden;` set. The default is false.
 * @ignore
 */
function tw2018func_scrollIntoView(element, spot, skipOverflowHiddenElements) {
  // Assuming offsetParent is available (it's not available when viewer is in
  // hidden iframe or object). We have to scroll: if the offsetParent is not set
  // producing the error. See also animationStartedClosure.
  let parent = element.parentElement;
  if (!parent) {
    console.error("offsetParent is not set -- cannot scroll");
    return;
  }

  const checkOverflow = skipOverflowHiddenElements || false;
  let offsetY = element.offsetTop + element.clientTop;
  let offsetX = element.offsetLeft + element.clientLeft;

  while (
    (parent.clientHeight === parent.scrollHeight &&
      parent.clientWidth === parent.scrollWidth) ||
    (checkOverflow && getComputedStyle(parent).overflow === "hidden")
  ) {
    offsetY += parent.offsetTop;
    offsetX += parent.offsetLeft;

    parent = parent.offsetParent;
    if (!parent) {
      return; // no need to scroll
    }
  }

  if (spot) {
    if (spot.top !== undefined) {
      offsetY += spot.top;
    }
    if (spot.left !== undefined) {
      offsetX += spot.left;
      parent.scrollLeft = offsetX;
    }
  }
  parent.scrollTop = offsetY;
}

/**
 * Provides "search" or "find" functionality for the PDF.
 * This object actually performs the search for a given string.
 * @ignore
 */
class PDFFindController {
  constructor(options) {
    this.startedTextExtraction = false;
    this.extractTextPromises = [];
    this.pendingPages = {};
    this.active = true; // If active, find results will be highlighted.
    this.pageContents = []; // Stores the text for each page.
    this.pageMatches = [];
    this.currentMatch = 0;
    this.matchCount = 0;
    this.selected = {
      // Currently selected match.
      pageIdx: -1,
      matchIdx: -1,
    };
    this.offset = {
      // Where the find algorithm currently is in the document.
      pageIdx: null,
      matchIdx: null,
    };
    this.pagesToSearch = null;
    this.resumePageIdx = null;
    this.state = {
      query: "",
    };
    this.dirtyMatch = false;
    this.findTimeout = null;
    this._app = options.app;
    this.integratedFind = options.integratedFind || false;
    this.charactersToNormalize = {
      "\u2018": "'", // Left single quotation mark
      "\u2019": "'", // Right single quotation mark
      "\u201A": "'", // Single low-9 quotation mark
      "\u201B": "'", // Single high-reversed-9 quotation mark
      "\u201C": '"', // Left double quotation mark
      "\u201D": '"', // Right double quotation mark
      "\u201E": '"', // Double low-9 quotation mark
      "\u201F": '"', // Double high-reversed-9 quotation mark
      "\u00BC": "1/4", // Vulgar fraction one quarter
      "\u00BD": "1/2", // Vulgar fraction one half
      "\u00BE": "3/4", // Vulgar fraction three quarters
    };
    this.findBar = options.findBar || null;

    // Compile the regular expression for text normalization once
    const replace = Object.keys(this.charactersToNormalize).join("");
    this.normalizationRegex = new RegExp("[" + replace + "]", "g");

    this.firstPagePromise = new Promise(function (resolve) {
      resolve();
    });
    this.handleEvent = this.handleEvent.bind(this);

    this._events = [
      "find",
      "findagain",
      "findhighlightallchange",
      "findcasesensitivitychange",
      "findwholewordchange",
    ];
  }

  setFindBar(findBar) {
    this.findBar = findBar;
    for (let i = 0, len = this._events.length; i < len; i++) {
      this.findBar.bar.addEventListener(this._events[i], this.handleEvent);
    }
  }

  dispose() {
    for (let i = 0, len = this._events.length; i < len; i++) {
      this.findBar.bar.removeEventListener(this._events[i], this.handleEvent);
    }
    this.reset();
  }

  reset() {
    this.startedTextExtraction = false;
    this.extractTextPromises = [];
    clearTimeout(this.findTimeout);
    // this.active = false;
  }

  normalize(text) {
    const me = this;
    return text.replace(this.normalizationRegex, function (ch) {
      return me.charactersToNormalize[ch];
    });
  }

  calculateMatch(pageIndex) {
    let pageContent = this.normalize(this.pageContents[pageIndex]);
    let query = this.normalize(this.state.query);
    const queryLen = query.length;

    if (queryLen === 0) {
      // Do nothing: the matches should be wiped out already.
      return;
    }

    if (!this.state.caseSensitive) {
      pageContent = pageContent.toLowerCase();
      query = query.toLowerCase();
    }

    const matches = [];
    let matchIdx = -queryLen;

    // this is _calculatePhraseMatch. PdfJs viewer has also
    // implemented _calculateWordMatch which is more complex
    while (true) {
      matchIdx = pageContent.indexOf(query, matchIdx + queryLen);
      if (matchIdx === -1) {
        break;
      }
      if (
        this.state.wholeWord &&
        !this._isEntireWord(pageContent, matchIdx, queryLen)
      ) {
        continue;
      }
      matches.push(matchIdx);
    }

    this.pageMatches[pageIndex] = matches;
    this.updatePage(pageIndex);
    if (this.resumePageIdx === pageIndex) {
      this.resumePageIdx = null;
      this.nextPageMatch();
    }

    // Update the matches count
    if (matches.length > 0) {
      this.matchCount += matches.length;
      this.updateUIResultsCount();
    }
  }

  /**
   * Determine if the search query constitutes a "whole word", by comparing the
   * first/last character type with the preceding/following character type.
   */
  _isEntireWord(content, startIdx, length) {
    if (startIdx > 0) {
      const first = content.charCodeAt(startIdx);
      const limit = content.charCodeAt(startIdx - 1);
      if (getCharacterType(first) === getCharacterType(limit)) {
        return false;
      }
    }
    const endIdx = startIdx + length - 1;
    if (endIdx < content.length - 1) {
      const last = content.charCodeAt(endIdx);
      const limit = content.charCodeAt(endIdx + 1);
      if (getCharacterType(last) === getCharacterType(limit)) {
        return false;
      }
    }
    return true;
  }

  extractText() {
    if (this.startedTextExtraction) {
      return;
    }

    this.startedTextExtraction = true;
    this.pageContents = [];

    const extractTextPromisesResolves = [];
    const numPages = this._app.viewer.pdfDoc.numPages;
    for (let i = 0; i < numPages; i++) {
      this.extractTextPromises.push(
        new Promise(function (resolve) {
          extractTextPromisesResolves.push(resolve);
        })
      );
    }

    const me = this;

    function extractPageText(pageIndex) {
      me._app.viewer
        .getPageTextContent(pageIndex)
        .then(function textContentResolved(textContent) {
          const textItems = textContent.items;
          const str = [];

          for (let i = 0, len = textItems.length; i < len; i++) {
            str.push(textItems[i].str);
          }

          // Store the pageContent as a string.
          me.pageContents.push(str.join(""));
          if (!me._app.viewer.pdfDoc) {
            return;
          }
          extractTextPromisesResolves[pageIndex](pageIndex);
          if (pageIndex + 1 < me._app.viewer.pdfDoc.numPages) {
            extractPageText(pageIndex + 1);
          }
        });
    }

    extractPageText(0);
  }

  handleEvent(e) {
    if (e.detail === null || e.type !== "findagain") {
      this.dirtyMatch = true;
    }
    this.state = e.detail;
    this.updateUIState(FindStatus.FIND_PENDING);

    this.firstPagePromise.then(
      function () {
        // this will be executed only once per opened document
        this.extractText();

        clearTimeout(this.findTimeout);
        if (e.type === "find") {
          // Only trigger the find action after 500ms of silence.
          this.findTimeout = setTimeout(this.nextMatch.bind(this), 500);
        } else {
          this.nextMatch();
        }
      }.bind(this)
    );
  }

  updatePage(index) {
    if (this.selected.pageIdx === index) {
      // If the page is selected, scroll the page into view, which triggers
      // rendering the page, which adds the textLayer. Once the textLayer is
      // build, it will scroll onto the selected match.
      this._app.viewer.showPage(index + 1);
    }

    const page = this._app.viewer.getPageView(index);
    if (this._app.viewer.modeView === 2) {
      // single view
      if (page.singleTextLayer) {
        page.singleTextLayer.updateMatches();
      }
    } else {
      // multi view
      if (page.textLayer) {
        page.textLayer.updateMatches();
      }
    }
  }

  nextMatch() {
    const previous = this.state.findPrevious;
    const currentPageIndex = this._app.viewer.currentPageNumber - 1;
    if (!this._app.viewer.pdfDoc) {
      return;
    }

    const numPages = this._app.viewer.pdfDoc.numPages;

    this.active = true;
    if (this.dirtyMatch) {
      // Need to recalculate the matches, reset everything.
      this.dirtyMatch = false;
      this.selected.pageIdx = this.selected.matchIdx = -1;
      this.currentMatch = 0;
      this.offset.pageIdx = currentPageIndex;
      this.offset.matchIdx = null;
      this.hadMatch = false;
      this.resumePageIdx = null;
      this.pageMatches = [];
      this.matchCount = 0;
      const me = this;

      for (let i = 0; i < numPages; i++) {
        // Wipe out any previous highlighted matches.
        this.updatePage(i);

        // As soon as the text is extracted start finding the matches in pages which aren't already in the pending state
        if (!(i in this.pendingPages)) {
          this.pendingPages[i] = true; // it could be whatever value, it is only important that exists that element
          this.extractTextPromises[i].then(function (pageIdx) {
            delete me.pendingPages[pageIdx];
            me.calculateMatch(pageIdx);
          });
        }
      }
    }

    // If there's no query there's no point in searching.
    if (this.state.query === "") {
      this.updateUIState(FindStatus.FIND_FOUND);
      return;
    }

    // If we're waiting on a page, we return since we can't do anything else.
    if (this.resumePageIdx) {
      return;
    }

    const offset = this.offset;
    // Keep track of how many pages we should maximally iterate through.
    this.pagesToSearch = numPages;
    // If there's already a matchIdx that means we are iterating through a
    // page's matches.
    if (offset.matchIdx !== null) {
      const numPageMatches = this.pageMatches[offset.pageIdx].length;
      if (
        (!previous && offset.matchIdx + 1 < numPageMatches) ||
        (previous && offset.matchIdx > 0)
      ) {
        // The simple case; we just have advance the matchIdx to select
        // the next match on the page.
        this.hadMatch = true;
        offset.matchIdx = previous ? offset.matchIdx - 1 : offset.matchIdx + 1;
        this.updateMatch(true);
        return;
      }
      // We went beyond the current page's matches, so we advance to
      // the next page.
      this.advanceOffsetPage(previous);
    }
    // Start searching through the page.
    this.nextPageMatch();
  }

  matchesReady(matches) {
    const offset = this.offset;
    const numMatches = matches.length;
    const previous = this.state.findPrevious;

    if (numMatches) {
      // There were matches for the page, so initialize the matchIdx.
      this.hadMatch = true;
      offset.matchIdx = previous ? numMatches - 1 : 0;
      this.updateMatch(true);
      return true;
    }

    // No matches, so attempt to search the next page.
    this.advanceOffsetPage(previous);
    if (offset.wrapped) {
      offset.matchIdx = null;
      if (this.pagesToSearch < 0) {
        // No point in wrapping again, there were no matches.
        this.updateMatch(false);
        // while matches were not found, searching for a page
        // with matches should nevertheless halt.
        return true;
      }
    }

    // Matches were not found (and searching is not done).
    return false;
  }

  /**
   * The method is called back from the text layer when match presentation
   * is updated.
   * @param {number} pageIndex - page index.
   * @param {number} index - match index.
   * @param {Array} elements - text layer div elements array.
   * @param {number} beginIdx - start index of the div array for the match.
   * @param {number} endIdx - end index of the div array for the match.
   */
  updateMatchPosition(pageIndex, index, elements, beginIdx, endIdx) {
    if (
      this.selected.matchIdx === index &&
      this.selected.pageIdx === pageIndex
    ) {
      const spot = {
        top: FIND_SCROLL_OFFSET_TOP,
        left: FIND_SCROLL_OFFSET_LEFT,
      };
      tw2018func_scrollIntoView(
        elements[beginIdx],
        spot,
        /* skipOverflowHiddenElements = */ true
      );
    }
  }

  nextPageMatch() {
    if (this.resumePageIdx !== null) {
      console.error("There can only be one pending page.");
    }

    let matches = [];
    do {
      const pageIdx = this.offset.pageIdx;
      matches = this.pageMatches[pageIdx];
      if (!matches) {
        // The matches don't exist yet for processing by "matchesReady",
        // so set a resume point for when they do exist.
        this.resumePageIdx = pageIdx;
        break;
      }
    } while (!this.matchesReady(matches));
  }

  advanceOffsetPage(previous) {
    const offset = this.offset;
    const numPages = this.extractTextPromises.length;
    offset.pageIdx = previous ? offset.pageIdx - 1 : offset.pageIdx + 1;
    offset.matchIdx = null;

    this.pagesToSearch--;

    if (offset.pageIdx >= numPages || offset.pageIdx < 0) {
      offset.pageIdx = previous ? numPages - 1 : 0;
      offset.wrapped = true;
    }
  }

  updateMatch(found) {
    let state = FindStatus.FIND_NOTFOUND;
    const wrapped = this.offset.wrapped;
    this.offset.wrapped = false;
    if (found) {
      const previousPage = this.selected.pageIdx;
      this.currentMatch = 0;
      for (let i = 0; i < this.offset.pageIdx; i++) {
        this.currentMatch += this.pageMatches[i].length;
      }

      this.selected.pageIdx = this.offset.pageIdx;
      this.currentMatch += this.offset.matchIdx + 1;
      this.selected.matchIdx = this.offset.matchIdx;
      state = wrapped ? FindStatus.FIND_WRAPPED : FindStatus.FIND_FOUND;
      // Update the currently selected page to wipe out any selected matches.
      if (previousPage !== -1 && previousPage !== this.selected.pageIdx) {
        this.updatePage(previousPage);
      }
    }

    this.updateUIState(state, this.state.findPrevious);
    if (this.selected.pageIdx !== -1) {
      this.updatePage(this.selected.pageIdx);
    }
  }

  updateUIResultsCount() {
    if (this.findBar === null) {
      throw new Error(
        "PDFFindController is not initialized with a PDFFindBar instance."
      );
    }

    this.findBar.updateResultsCount(this.currentMatch, this.matchCount);
  }

  _shouldDirtyMatch(cmd, state) {
    // When the search query changes, regardless of the actual search command
    // used, always re-calculate matches to avoid errors (fixes bug 1030622).
    if (state.query !== this._state.query) {
      return true;
    }
    switch (cmd) {
      case "findagain":
        const pageNumber = this._selected.pageIdx + 1;
        const linkService = this._linkService;
        // Only treat a 'findagain' event as a new search operation when it's
        // *absolutely* certain that the currently selected match is no longer
        // visible, e.g. as a result of the user scrolling in the document.
        //
        // NOTE: If only a simple `this._linkService.page` check was used here,
        // there's a risk that consecutive 'findagain' operations could "skip"
        // over matches at the top/bottom of pages thus making them completely
        // inaccessible when there's multiple pages visible in the viewer.
        if (
          pageNumber >= 1 &&
          pageNumber <= linkService.pagesCount &&
          pageNumber !== linkService.page &&
          !linkService.isPageVisible(pageNumber)
        ) {
          return true;
        }
        return false;
      case "findhighlightallchange":
        return false;
    }
    return true;
  }

  updateUIState(findStatus, previous) {
    if (this.findBar === null) {
      throw new Error(
        "PDFFindController is not initialized with a PDFFindBar instance."
      );
    }

    this.findBar.updateUIState(
      findStatus,
      previous,
      this.matchCount,
      this.currentMatch
    );
  }
}

export { FindStatus, PDFFindController };
